<script setup lang="ts">
  import { ref, watchEffect, computed } from 'vue'
  import { useWindowSize } from '@vueuse/core'
  import { calculateAutoLayoutColumnsForWidth } from '../../utils/layout/calculateAutoLayoutColumnsForWidth'
  import { useRedirectActions } from '../../composables/useRedirectActions'

  const { sendToNewsPage } = useRedirectActions()
  const directAdCampaigns = await useDirectAds()
  const isLoading = ref(true)
  const articles = ref<NewsArticle[]>([])
  const { selectedCategory } = useSearchParams()
  const emit = defineEmits<{
    'used-articles': [count: number]
  }>()
  const { headlines } = useNewsArticleHeadlines(selectedCategory)
  const { width } = useWindowSize()
  const contentDisplaySettingsStore = useContentDisplaySettingsStore()
  const { featureFlags } = storeToRefs(contentDisplaySettingsStore)

  const columns = computed(() => {
    const columnsForWidth = calculateAutoLayoutColumnsForWidth(width.value)
    // AutoLayout columns sizes minus the space for the 2x2 tile
    return columnsForWidth - 2
  })

  const adPositions = computed(() => {
    if (columns.value === 3) return [0, 2]
    if (columns.value === 2) return [1, 2]
    return [0]
  })

  const displayedArticles = computed(() => {
    const start = 1 // Skip first article (used in 2x2 tile)
    const maxArticlesNeeded = columns.value * 2 - adPositions.value.length // Adjust for ads
    return headlines.value?.slice(start, start + maxArticlesNeeded) || []
  })

  const gridClass = computed(() => {
    const baseClasses = 'grid row-span-2 gap-12px'
    const colClasses: { [key: number]: string } = {
      1: 'grid-cols-1',
      2: 'grid-cols-2',
      3: 'grid-cols-3',
    }
    return `${baseClasses} ${colClasses[columns.value]}`
  })

  const getArticleProps = (article: NewsArticle) => ({
    id: article.id,
    headline: article.title,
    subtitle: article.subtitle,
    'thumbnail-url': article.thumbnailUrl ?? '',
    'publisher-name': article.provider,
    'published-date': article.publishedDate,
    'publisher-icon-url': article.providerLogoUrl,
    'article-url': article.link,
    design: 'msn' as const,
    role: 'article',
  })

  const getArticleIndex = (position: number) => {
    const precedingAds = adPositions.value.filter(
      (pos) => pos < position
    ).length

    // Adjust index based on position and preceding ads
    return position - precedingAds
  }

  const adClasses = computed<Record<number, string>>(() => {
    const adConfig =
      columns.value === 3
        ? featureFlags.value.aboveFoldSectionAds?.grid3
        : columns.value === 2
          ? featureFlags.value.aboveFoldSectionAds?.grid2
          : featureFlags.value.aboveFoldSectionAds?.grid1

    //map of position to class name
    const classMap: Record<number, string> = {}
    adConfig?.forEach((ad: AdBase) => {
      // Convert y,x coordinates to grid position
      const position = (ad.y - 1) * columns.value + (ad.x - 1)
      classMap[position] = ad.classes
    })

    return classMap
  })

  const getMatchingDirectAd = (position: number) => {
    const adClass = adClasses.value[position]
    if (!adClass || !directAdCampaigns?.length) return undefined
    const matchingCampaign = directAdCampaigns.find(
      (campaign) => campaign.creatives[0]?.placement === adClass
    )

    return matchingCampaign?.creatives[0]
  }

  watchEffect(
    () => {
      if (!headlines.value?.length) return

      articles.value = headlines.value.slice(0, 7)
      isLoading.value = false

      const totalSlots = columns.value * 2
      const nonAdSlots = totalSlots - adPositions.value.length
      emit('used-articles', 1 + nonAdSlots)
    },
    { flush: 'post' }
  )
</script>

<template v-if="!isLoading">
  <div
    class="flex gap-12px py-8px bg-secondary items-center justify-center"
    :class="[columns > 0 ? 'flex-row' : 'flex-col']"
  >
    <!-- 2x2 Tile -->
    <div class="row-span-2">
      <ArticleCard
        v-if="articles[0]"
        v-bind="getArticleProps(articles[0])"
        variant="2x2-top-headline"
        @click="sendToNewsPage"
      />
    </div>

    <!-- rows of regular article cards & ads -->
    <div v-show="columns > 0" :class="gridClass">
      <template v-for="index in columns * 2" :key="index">
        <div class="h-full">
          <ArticleCard
            v-if="adPositions.includes(index - 1)"
            :ad="true"
            :creative="getMatchingDirectAd(index - 1)"
            design="msn"
            variant="default"
            role="article"
            :class="getMatchingDirectAd(index - 1) ? '' : adClasses[index - 1]"
          />
          <ArticleCard
            v-else-if="displayedArticles[getArticleIndex(index - 1)]"
            v-bind="
              getArticleProps(displayedArticles[getArticleIndex(index - 1)])
            "
            variant="default"
            @click="sendToNewsPage"
          />
        </div>
      </template>
    </div>
  </div>
</template>
